import { useEffect, useState } from 'react';
import axios from 'axios';
import Cookies from 'universal-cookie';
import { Link as RouterLink } from 'react-router-dom';
import {
    Card,
    Container,
    Stack,
    Typography,
    Box,
    Button,
    TextField,
    MenuItem,
    Table,
    TableHead,
    TableRow,
    TableBody,
    TableCell,
    Chip,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import { API_URL } from '../../api';
import AccountStatusChips from '../../components/AccountStatusChips';
import HowItWorksCard from '../../components/HowItWorksCard';

const cookies = new Cookies();
const authHeaders = () => ({ headers: { Authorization: cookies.get('TOKEN') } });

const ROLE_LABEL = { parent: 'Parent', teacher: 'Tutor', student: 'Student' };
const fmt = (d) => (d ? new Date(d).toLocaleString('en-GB') : '—');

const detailHref = (a) => {
    if (a.role === 'parent') return `/parents/${a._id}`;
    if (a.role === 'student') return `/students/${a._id}`;
    if (a.role === 'teacher') return `/teacher/updateTeacher/${a._id}`;
    return null;
};

// Role-specific summary of the profile info admin has on file — what they'd
// otherwise have to click into the account to see. Keeps the queue actionable
// from the row.
const roleSummary = (a) => {
    if (a.role === 'parent') {
        const c = a.childInfo || {};
        const parts = [];
        if (c.studentName) parts.push(<><strong>Child:</strong> {c.studentName}</>);
        if (c.yearGroup) parts.push(<><strong>Year:</strong> {c.yearGroup}</>);
        if (c.level) parts.push(<><strong>Level:</strong> {c.level}</>);
        if (c.subject) parts.push(<><strong>Subject:</strong> {c.subject}</>);
        if (c.school) parts.push(<><strong>School:</strong> {c.school}</>);
        if (a.phone) parts.push(<><strong>Phone:</strong> {a.phone}</>);
        if (a.isExistingParent) parts.push(<Chip key="ep" size="small" label="Existing parent" color="info" variant="outlined" sx={{ ml: 0.5 }} />);
        return parts.length ? parts : [<em key="e">No profile info supplied</em>];
    }
    if (a.role === 'student') {
        const parts = [];
        if (a.yearGroup) parts.push(<><strong>Year:</strong> {a.yearGroup}</>);
        if (a.level) parts.push(<><strong>Level:</strong> {a.level}</>);
        if (a.subject) parts.push(<><strong>Subject:</strong> {a.subject}</>);
        if (a.school) parts.push(<><strong>School:</strong> {a.school}</>);
        if (a.phone) parts.push(<><strong>Phone:</strong> {a.phone}</>);
        return parts.length ? parts : [<em key="e">No profile info supplied</em>];
    }
    if (a.role === 'teacher') {
        const subs = a.teachingSubjects || [];
        const parts = [];
        if (subs.length) {
            const label = subs.map((s) => `${s.subject}${s.levels?.length ? ` (${s.levels.join(', ')})` : ''}`).join(' · ');
            parts.push(<><strong>Teaches:</strong> {label}</>);
        }
        if (a.phone) parts.push(<><strong>Phone:</strong> {a.phone}</>);
        return parts.length ? parts : [<em key="e">No teaching subjects declared</em>];
    }
    return [];
};

export default function PendingApprovalsPage() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [roleFilter, setRoleFilter] = useState('');
    const [busyId, setBusyId] = useState(null);

    // Decline dialog
    const [declineFor, setDeclineFor] = useState(null);
    const [declineReason, setDeclineReason] = useState('');
    const [declineNotify, setDeclineNotify] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const q = roleFilter ? `?role=${roleFilter}` : '';
            const { data } = await axios.get(
                `${API_URL}/api/admin/accounts/pending${q}`,
                authHeaders()
            );
            setItems(data?.items || []);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to load.');
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [roleFilter]);

    const approve = async (account) => {
        setBusyId(account._id);
        try {
            await axios.post(
                `${API_URL}/api/admin/accounts/${account._id}/approve`,
                { sendEmail: true },
                authHeaders()
            );
            setMessage(`${account.firstName} ${account.surname} approved — they'll get a welcome email.`);
            load();
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed.');
        } finally {
            setBusyId(null);
        }
    };

    const openDecline = (account) => {
        setDeclineFor(account);
        setDeclineReason('');
        setDeclineNotify(false);
    };

    const submitDecline = async () => {
        if (!declineFor) return;
        if (!declineReason.trim()) {
            setError('A reason is required when declining.');
            return;
        }
        setBusyId(declineFor._id);
        try {
            await axios.post(
                `${API_URL}/api/admin/accounts/${declineFor._id}/decline`,
                { reason: declineReason.trim(), sendEmail: declineNotify },
                authHeaders()
            );
            setMessage(`${declineFor.firstName} ${declineFor.surname} marked as declined.`);
            setDeclineFor(null);
            load();
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed.');
        } finally {
            setBusyId(null);
        }
    };

    return (
        <Container>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
                <Box sx={{ maxWidth: 900 }}>
                    <Typography variant="h4">Pending approvals</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Accounts that were created through <em>Register</em> on the public site
                        and are waiting for you to vet them. Use the profile summary in each row to
                        decide, or click the person's name to open their full account page.
                    </Typography>
                    
                </Box>
            </Stack>
            <Box sx={{ mb: 3 }}>
                <HowItWorksCard>
                        <Typography variant="caption" component="div">
                            <strong>Where do these come from?</strong> Anyone who clicks{' '}
                            <em>Register</em> on the public website (parent, student or tutor)
                            lands here after they verify their email. Waiting-list enquiries are a
                            separate flow — those are handled from <em>Waiting list</em>, and
                            accounts you create from there are auto-Approved and never appear
                            in this queue.<br />
                            <strong>Approve</strong> — flips the account to <em>Approved</em>{' '}
                            and (by default) sends a welcome email. They can now use the portal
                            fully (see lessons, invoices, progress reports).<br />
                            <strong>Decline</strong> — flips the account to <em>Declined</em>. A
                            reason is required and kept in the account's status history for
                            audit. The user can still log in, but sees a "your account has been
                            declined" banner and portal features are hidden. Optionally you can
                            tick to notify them by email. You can reverse the decision any time
                            from the account detail page.<br />
                            <strong>What to look at in the row:</strong> the role, the profile
                            summary (child info for parents, level/subject/school for students,
                            teaching subjects for tutors), and whether they've verified their
                            email — unverified accounts can't log in even once you approve them,
                            so it's usually safer to wait until they do.
                        </Typography>
                    </HowItWorksCard>
            </Box>


            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
            {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}

            <Card sx={{ p: 2, mb: 3 }}>
                <TextField
                    select size="small" label="Role filter"
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value)}
                    sx={{ minWidth: 220 }}
                >
                    <MenuItem value="">All roles</MenuItem>
                    <MenuItem value="parent">Parents</MenuItem>
                    <MenuItem value="teacher">Tutors</MenuItem>
                    <MenuItem value="student">Students</MenuItem>
                </TextField>
            </Card>

            <Card>
                {loading ? (
                    <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>
                ) : (
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Registered</TableCell>
                                <TableCell>Role</TableCell>
                                <TableCell>Name / Email</TableCell>
                                <TableCell>Profile</TableCell>
                                <TableCell>Verified</TableCell>
                                <TableCell align="right" sx={{ width: 220 }} />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.map((a) => {
                                const href = detailHref(a);
                                const summary = roleSummary(a);
                                return (
                                    <TableRow key={a._id} sx={{ verticalAlign: 'top' }}>
                                        <TableCell>{fmt(a.createdAt)}</TableCell>
                                        <TableCell>
                                            <Chip size="small" label={ROLE_LABEL[a.role] || a.role} variant="outlined" />
                                        </TableCell>
                                        <TableCell>
                                            {href ? (
                                                <Box
                                                    component={RouterLink}
                                                    to={href}
                                                    sx={{
                                                        color: 'primary.main',
                                                        textDecoration: 'none',
                                                        fontWeight: 500,
                                                        '&:hover': { textDecoration: 'underline' },
                                                    }}
                                                >
                                                    {a.firstName} {a.surname}
                                                </Box>
                                            ) : (
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    {a.firstName} {a.surname}
                                                </Typography>
                                            )}
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                {a.email}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Stack spacing={0.25}>
                                                {summary.map((line, i) => (
                                                    <Typography key={i} variant="caption" color="text.secondary">
                                                        {line}
                                                    </Typography>
                                                ))}
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            {a.emailVerifiedAt
                                                ? <Chip size="small" label="Verified" color="success" variant="outlined" />
                                                : <Chip size="small" label="Not verified" color="warning" variant="outlined" />}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                <Button
                                                    size="small" variant="contained"
                                                    startIcon={<CheckCircleRoundedIcon />}
                                                    disabled={busyId === a._id}
                                                    onClick={() => approve(a)}
                                                >
                                                    Approve
                                                </Button>
                                                <Button
                                                    size="small" color="warning"
                                                    startIcon={<BlockRoundedIcon />}
                                                    disabled={busyId === a._id}
                                                    onClick={() => openDecline(a)}
                                                >
                                                    Decline
                                                </Button>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            {items.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6}>
                                        <Typography align="center" color="text.secondary" sx={{ py: 3 }}>
                                            No pending approvals right now.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                )}
            </Card>

            <Dialog open={!!declineFor} onClose={() => setDeclineFor(null)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    Decline {declineFor ? `${declineFor.firstName} ${declineFor.surname}` : 'account'}
                </DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            A reason is recorded in the account's status history. You can flip them
                            back to Approved later from the account detail page.
                        </Typography>
                        <TextField
                            label="Reason"
                            value={declineReason}
                            onChange={(e) => setDeclineReason(e.target.value)}
                            multiline
                            minRows={2}
                            autoFocus
                            placeholder="e.g. Duplicate signup — already have an account on file"
                        />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <input
                                type="checkbox"
                                checked={declineNotify}
                                onChange={(e) => setDeclineNotify(e.target.checked)}
                                id="decline-notify"
                            />
                            <Typography component="label" htmlFor="decline-notify" variant="body2">
                                Email the user about the decision (default: off)
                            </Typography>
                        </Box>
                        {declineFor && (
                            <AccountStatusChips account={declineFor} show="account" />
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeclineFor(null)}>Cancel</Button>
                    <Button
                        onClick={submitDecline}
                        variant="contained" color="warning"
                        disabled={!declineReason.trim() || busyId === declineFor?._id}
                    >
                        Decline account
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}
